import moment from "moment";
import { ModalTopSection } from "../../../components/DetailsModal/ModalTopSection";
import {
  CopyButton,
  DataLoadingView,
  ErrorOccuredView,
  ModalContainer,
  SectionSubheader,
  SwitchAppButton,
} from "../../../components";
import { ModalSectionTitle } from "../../../components";
import { LeftRightFlexedContainer } from "../../../components";
import { CurrencyDisplay } from "../../../components";
import { Badge } from "../../../components";
import { IVirtualAccountTransferRefundDetailsModalViewProps } from "./VirtualAccountTransferRefundDetailsModal";
import {
  formatNumberWithCommas,
  getPaymentStatusIndicator,
  getVirtualAccountTransferRefundStatusIndicator,
  truncateStringWithEllipsis,
} from "../../../utils";
import { appConfig } from "../../../configs";
import { Link } from "react-router-dom";
import {
  chargeBearer,
  paymentIssueTypes,
  transactionStatus,
} from "../../../enums";
import { AcceptAsPartialPaymentForm } from "./AcceptAsPartialPaymentForm";
import { PaymentIssueResolutionViewModeSelector } from "./PaymentIssueResolutionViewModeSelector";
import { RefundToMerchantBalanceForm } from "./RefundToMerchantBalanceForm";
import { RedirectToInvoiceForm } from "./RedirectToInvoiceForm";
import { RefundToCustomerForm } from "./RefundToCustomerForm";

export const View = (
  props: IVirtualAccountTransferRefundDetailsModalViewProps
) => {
  const isPaymentIssueCompleted =
    props.modalData && props.modalData.is_refund_completed;

  const isUnapprovedDuplicatePayment =
    props.modalData &&
    props.modalData.payment_issue_type === paymentIssueTypes.duplicatePayment &&
    !props.modalData.is_duplicate_payment_resolution_switchapp_approved;

  const resolvePaymentIssueFormDisabledReason = isPaymentIssueCompleted
    ? "Payment issue has already been resolved"
    : isUnapprovedDuplicatePayment
    ? "Duplicate payment issues require approval by SwitchApp first. Please contact SwitchApp Admin."
    : null;

  const canAcceptAsPartialPayment =
    !isPaymentIssueCompleted &&
    !isUnapprovedDuplicatePayment &&
    props.sourceTxDetails &&
    ((!props.sourceTxDetails.is_child_partial_payment &&
      props.sourceTxDetails.status !== transactionStatus.successful) ||
      (props.sourceTxDetails.is_child_partial_payment &&
        props.parentTxDetails &&
        props.parentTxDetails.status !== transactionStatus.successful));

  const acceptAsPartialFormDisabledReason = isPaymentIssueCompleted ? (
    "Payment issue has already been resolved"
  ) : isUnapprovedDuplicatePayment ? (
    "Duplicate payment issues require approval by SwitchApp first. Please contact SwitchApp Admin."
  ) : props.parentTxDetails?.status === transactionStatus.successful ? (
    <>
      This parent transaction has already been completed. <br />
      Please use one of the other options above to initiate a refund directly to
      the customer or settle to your merchant balance. <br />
    </>
  ) : !props.sourceTxDetails?.is_child_partial_payment &&
    props.sourceTxDetails?.status === transactionStatus.successful ? (
    <>
      This transaction has already been completed. <br />
      Please use one of the other options above to initiate a refund directly to
      the customer or settle to your merchant balance. <br />
    </>
  ) : props.modalData?.payment_issue_type !==
      paymentIssueTypes.amountIncomplete &&
    props.modalData?.payment_issue_type !==
      paymentIssueTypes.duplicatePayment ? (
    "Only payment issues of [AMOUNT INCOMPLETE] can be accepted as partial payments."
  ) : canAcceptAsPartialPayment ? null : (
    "This payment refund cannot be accepted as a partial payment"
  );

  const amountTransferredCalculated = !props.modalData
    ? 0
    : props.modalData.payment_issue_type ===
        paymentIssueTypes.amountIncomplete ||
      props.modalData.payment_issue_type === paymentIssueTypes.duplicatePayment
    ? Number(props.modalData.refund_amount)
    : Number(props.modalData.refund_amount) +
      Number(props.modalData.charged_amount);

  const statusIndicator =
    props.modalData &&
    getVirtualAccountTransferRefundStatusIndicator(props.modalData);

  const txStatusIndicator =
    props.sourceTxDetails && getPaymentStatusIndicator(props.sourceTxDetails);

  const parentTxStatusIndicator =
    props.parentTxDetails && getPaymentStatusIndicator(props.parentTxDetails);

  return (
    <div className="payment-issue-details-container">
      <ModalContainer
        isModalOpen={props.isModalOpen}
        closeModal={props.closeModal}
        className="payment-issue-details-modal-container"
        modalHeader={
          !props.modalData ? (
            <SectionSubheader title="PAYMENT ISSUE DETAILS" />
          ) : (
            <ModalTopSection
              header="PAYMENT ISSUE DETAILS"
              subHeader={
                <div className="sub-header flex-container justify-content--start mt-5">
                  <div className="mr-5">
                    <CurrencyDisplay
                      currencyOrSymbol={props.sourceTxDetails?.currency ?? ""}
                      amount={props.modalData.refund_amount}
                    />
                  </div>

                  <Badge
                    text={statusIndicator!.statusText}
                    statusColor={statusIndicator!.statusType}
                  />
                </div>
              }
              copyText="Payment Issue ID"
              copyContent={props.modalData.id}
              closeModal={props.closeModal}
            />
          )
        }
      >
        {isPaymentIssueCompleted ? null : (
          <PaymentIssueResolutionViewModeSelector
            acceptAsPartialPaymentView={
              <AcceptAsPartialPaymentForm
                isLoadingFormData={
                  props.isLoading ||
                  props.isLoadingSourceTxDetails ||
                  props.isLoadingParentTxDetails
                }
                isFormEnabled={!!canAcceptAsPartialPayment}
                formDisabledReason={acceptAsPartialFormDisabledReason}
                refundAmount={props.modalData?.refund_amount || 0}
                refundCurrency={props.sourceTxDetails?.currency || "N/A"}
                destinationTransactionOrParent={
                  props.sourceTxDetails?.is_child_partial_payment
                    ? props.parentTxDetails
                    : props.sourceTxDetails
                }
                transferRefundId={props.modalData?.id || ""}
                transactionId={props.modalData?.transaction_id || ""}
                sourceTxBearer={
                  props.sourceTxDetails?.bearer || chargeBearer.merchant
                }
              />
            }
            refundToCustomerView={
              <RefundToCustomerForm
                isLoadingFormData={
                  props.isLoading ||
                  props.isLoadingSourceTxDetails ||
                  props.isLoadingParentTxDetails
                }
                isFormEnabled={!isPaymentIssueCompleted}
                formDisabledReason={resolvePaymentIssueFormDisabledReason}
                refundAmount={props.modalData?.refund_amount || 0}
                refundCurrency={props.sourceTxDetails?.currency || "N/A"}
                transferRefundId={props.modalData?.id || ""}
              />
            }
            redirectToInvoiceView={
              <RedirectToInvoiceForm
                isLoadingFormData={
                  props.isLoading ||
                  props.isLoadingSourceTxDetails ||
                  props.isLoadingParentTxDetails
                }
                isFormEnabled={!isPaymentIssueCompleted}
                formDisabledReason={resolvePaymentIssueFormDisabledReason}
                refundAmount={props.modalData?.refund_amount || 0}
                refundCurrency={props.sourceTxDetails?.currency || "N/A"}
                transferRefundId={props.modalData?.id || ""}
                sourceTxBearer={
                  props.sourceTxDetails?.bearer || chargeBearer.merchant
                }
                customer={props.sourceTxDetails?.customer}
              />
            }
            settleToMerchantBalanceView={
              <RefundToMerchantBalanceForm
                isLoadingFormData={
                  props.isLoading ||
                  props.isLoadingSourceTxDetails ||
                  props.isLoadingParentTxDetails
                }
                isFormEnabled={!isPaymentIssueCompleted}
                formDisabledReason={resolvePaymentIssueFormDisabledReason}
                refundAmount={props.modalData?.refund_amount || 0}
                refundCurrency={props.sourceTxDetails?.currency || "N/A"}
                transferRefundId={props.modalData?.id || ""}
              />
            }
          />
        )}

        {!props.modalData ? (
          <>
            {props.isLoading ? (
              <DataLoadingView message={"Loading refund details"} />
            ) : props.isError || !props.modalData ? (
              <ErrorOccuredView
                message={
                  "An error occured while loading the refund details. Please retry..."
                }
              />
            ) : null}
          </>
        ) : (
          <>
            <div>
              <ModalSectionTitle
                key_id="Refund Details"
                value={
                  <Link
                    to={`/payments/virtual-account-transfer-refunds`}
                    className="switchapp-link"
                  >
                    View all
                  </Link>
                }
              />

              <LeftRightFlexedContainer
                key_id="Refund ID"
                value={
                  <div className="flex-container justify-content--start align-items--center">
                    <div className="transaction-id-text modal-header--title">
                      <span title={props.modalData.id}>
                        {truncateStringWithEllipsis(props.modalData.id, 25)}
                      </span>
                    </div>
                    <div className="copy-button--container ml-5">
                      <CopyButton
                        stringToCopy={props.modalData.id}
                        iconDescription="copy ID"
                        className="copy-btn"
                      />
                    </div>
                  </div>
                }
              />

              <LeftRightFlexedContainer
                key_id="Reference"
                value={
                  <div className="flex-container justify-content--start align-items--center">
                    <div className="transaction-id-text modal-header--title">
                      {props.modalData.tx_ref}
                    </div>
                    <div className="copy-button--container ml-5">
                      <CopyButton
                        stringToCopy={props.modalData.tx_ref}
                        iconDescription="copy reference"
                        className="copy-btn disable-show-details"
                      />
                    </div>
                  </div>
                }
              />

              <LeftRightFlexedContainer
                key_id="Refund Amount"
                value={
                  <>
                    <CurrencyDisplay
                      currencyOrSymbol={props.sourceTxDetails?.currency ?? ""}
                      amount={props.modalData.refund_amount}
                      amountFontSize={16}
                      symbolFontSize={12}
                    />
                  </>
                }
              />

              <LeftRightFlexedContainer
                key_id="Amount Transferred"
                value={
                  <>
                    <CurrencyDisplay
                      currencyOrSymbol={props.sourceTxDetails?.currency ?? ""}
                      amount={amountTransferredCalculated ?? 0}
                      amountFontSize={16}
                      symbolFontSize={12}
                    />
                  </>
                }
              />

              <LeftRightFlexedContainer
                key_id="Refund Status"
                value={
                  <Badge
                    text={statusIndicator!.statusText}
                    statusColor={statusIndicator!.statusType}
                  />
                }
              />
              <LeftRightFlexedContainer
                key_id="Refund Reason"
                value={props.modalData.refund_reason || "..."}
              />

              {!props.modalData.issues_resolution_tracker_record ? null : (
                <>
                  <LeftRightFlexedContainer
                    key_id="Issue Resolution"
                    value={
                      props.modalData.issues_resolution_tracker_record
                        .issue_resolution_type || "..."
                    }
                  />
                  <LeftRightFlexedContainer
                    key_id="Resolution Description"
                    value={
                      props.modalData.issues_resolution_tracker_record
                        .resolution_description || "..."
                    }
                  />
                </>
              )}
            </div>

            {!props.sourceTxDetails ||
            !props.sourceTxDetails.is_child_partial_payment ? null : (
              <>
                <ModalSectionTitle
                  key_id="Parent Transaction Details"
                  value={""}
                />

                {!props.parentTxDetails ? (
                  <>
                    {props.isLoadingParentTxDetails ? (
                      <DataLoadingView
                        message={"Loading parent partial payment details..."}
                      />
                    ) : props.isErrorParentTxDetails ||
                      !props.parentTxDetails ? (
                      <ErrorOccuredView
                        message={
                          "An error occured while loading the parent transaction. Please retry..."
                        }
                      />
                    ) : null}
                  </>
                ) : (
                  <>
                    <div>
                      <LeftRightFlexedContainer
                        key_id="Parent Tx Reference"
                        value={
                          <Link
                            to={`/payments/${props.parentTxDetails.id}/details`}
                            className="transaction-id-text modal-header--title"
                          >
                            <div className="flex-container justify-content--start align-items--center">
                              <div className="transaction-id-text modal-header--title">
                                {props.parentTxDetails.tx_ref}
                              </div>
                              <div className="copy-button--container ml-5">
                                <CopyButton
                                  stringToCopy={props.parentTxDetails.tx_ref}
                                  iconDescription="copy reference"
                                  className="copy-btn disable-show-details"
                                />
                              </div>
                            </div>
                          </Link>
                        }
                      />

                      <LeftRightFlexedContainer
                        key_id="Status"
                        value={
                          <Badge
                            text={parentTxStatusIndicator!.statusText}
                            statusColor={parentTxStatusIndicator!.statusType}
                          />
                        }
                      />

                      <LeftRightFlexedContainer
                        key_id="Tx Amount"
                        value={
                          <>
                            <CurrencyDisplay
                              currencyOrSymbol={props.parentTxDetails.currency}
                              amount={props.parentTxDetails.amount ?? 0}
                              amountFontSize={16}
                              symbolFontSize={12}
                            />{" "}
                            ({props.parentTxDetails.currency})
                          </>
                        }
                      />

                      <LeftRightFlexedContainer
                        key_id="Amount Paid"
                        value={
                          <>
                            <CurrencyDisplay
                              currencyOrSymbol={props.parentTxDetails.currency}
                              amount={props.parentTxDetails.amount_paid ?? 0}
                              amountFontSize={16}
                              symbolFontSize={12}
                            />{" "}
                            ({props.parentTxDetails.currency})
                          </>
                        }
                      />

                      {!props.parentTxDetails.partial_payments_count ? null : (
                        <LeftRightFlexedContainer
                          key_id="Partial Payments Count"
                          value={formatNumberWithCommas(
                            props.parentTxDetails.partial_payments_count
                          )}
                        />
                      )}
                    </div>
                  </>
                )}
              </>
            )}
            <ModalSectionTitle
              key_id={`Source ${
                props.sourceTxDetails?.is_child_partial_payment
                  ? "Partial Payment"
                  : "Transaction"
              } Details`}
              value={""}
            />

            {!props.sourceTxDetails ? (
              <>
                {props.isLoadingSourceTxDetails ? (
                  <DataLoadingView
                    message={"Loading source transaction details"}
                  />
                ) : props.isErrorSourceTxDetails || !props.sourceTxDetails ? (
                  <ErrorOccuredView
                    message={
                      "An error occured while loading the source transaction. Please retry..."
                    }
                  />
                ) : null}
              </>
            ) : (
              <>
                <div>
                  <LeftRightFlexedContainer
                    key_id="Tx Reference"
                    value={
                      <Link
                        to={`/payments/${props.sourceTxDetails.id}/details`}
                        className="transaction-id-text modal-header--title"
                      >
                        <div className="flex-container justify-content--start align-items--center">
                          <div className="transaction-id-text modal-header--title">
                            {props.sourceTxDetails.tx_ref}
                          </div>
                          <div className="copy-button--container ml-5">
                            <CopyButton
                              stringToCopy={props.sourceTxDetails.tx_ref}
                              iconDescription="copy reference"
                              className="copy-btn disable-show-details"
                            />
                          </div>
                        </div>
                      </Link>
                    }
                  />

                  <LeftRightFlexedContainer
                    key_id="Tx Status"
                    value={
                      <Badge
                        text={txStatusIndicator!.statusText}
                        statusColor={txStatusIndicator!.statusType}
                      />
                    }
                  />

                  <LeftRightFlexedContainer
                    key_id="Charged Amount"
                    value={
                      <>
                        <CurrencyDisplay
                          currencyOrSymbol={props.sourceTxDetails.currency}
                          amount={props.sourceTxDetails.charged_amount ?? 0}
                          amountFontSize={16}
                          symbolFontSize={12}
                        />{" "}
                        ({props.sourceTxDetails.currency})
                      </>
                    }
                  />
                </div>

                <div>
                  <ModalSectionTitle
                    key_id="Transaction Customer Details"
                    value={""}
                  />

                  <LeftRightFlexedContainer
                    key_id="Customer name"
                    value={
                      props.sourceTxDetails.customer.full_name || "(Anonymous)"
                    }
                  />
                  <LeftRightFlexedContainer
                    key_id="Customer email"
                    value={
                      <div className="flex-container justify-content--start align-items--center no-wrap">
                        <div className="transaction-id-text modal-header--title">
                          <span title={props.sourceTxDetails.customer.email}>
                            {props.sourceTxDetails.customer.email}
                          </span>
                        </div>
                        <div className="copy-button--container ml-3">
                          <CopyButton
                            // name="copy-customer-button"
                            stringToCopy={props.sourceTxDetails.customer.email}
                            iconDescription="copy customer email"
                            className="copy-btn"
                          />
                        </div>
                      </div>
                    }
                  />
                  <LeftRightFlexedContainer
                    key_id="Customer Phone"
                    value={props.sourceTxDetails.customer.phone_number}
                  />
                  <LeftRightFlexedContainer
                    key_id="Customer Address"
                    value={props.sourceTxDetails.customer.address}
                  />
                  <LeftRightFlexedContainer
                    key_id="Date added"
                    value={
                      <span className="date--oneline">
                        {moment(props.sourceTxDetails.created_at).format(
                          appConfig.dateTimeFormat
                        )}
                      </span>
                    }
                  />
                </div>
              </>
            )}
          </>
        )}
      </ModalContainer>
    </div>
  );
};
